import type { TFunction } from 'i18next';
import type { AlertmanagerAlert, Incident, OpsOverview, OpsService, Slo } from '../../api';
import type { Tone } from '../../components/Badge';
import { formatCount, formatPercent, formatRelative } from '../../lib/format';

/**
 * Client-side status evaluation for the Operations overview, from
 * `GET /ops/v1/overview` (point-in-time counts from the observability views) and
 * the other read-only Observability API lists.
 *
 * Thresholds mirror observability/alerts where a matching rule exists
 * (LedgerImbalanceNonZero, RegistryUnknownTransactionsRising, ReconciliationBacklogHigh,
 * WorkflowsStuckManualReview, OutboxBacklog, CriticalQueueAgeHigh); the rest are
 * provisional.
 */

export type MetricStatus = 'ok' | 'warn' | 'crit' | 'unknown';

export type TileGroup = 'registry' | 'money' | 'compliance' | 'response' | 'platform';

export type OpsT = TFunction<'ops'>;

export interface MetricTile {
  id: string;
  group: TileGroup;
  label: string;
  value: string;
  status: MetricStatus;
  caption: string;
  /** Extra line shown when the tile is not ok. */
  alert?: string;
  to: string;
  toLabel: string;
  /** The Prometheus alert rule that fires for the same condition, if any. */
  alertName?: string;
}

export const STATUS_TONE: Record<MetricStatus, Tone> = { ok: 'ok', warn: 'warn', crit: 'crit', unknown: 'neutral' };

function countStatus(value: number, warnAbove: number, critAtOrAbove: number): MetricStatus {
  if (!Number.isFinite(value)) return 'unknown';
  if (value >= critAtOrAbove) return 'crit';
  return value > warnAbove ? 'warn' : 'ok';
}

export const sumValues = (r: Record<string, number>): number => Object.values(r).reduce((a, b) => a + Number(b), 0);
const pick = (r: Record<string, number>, keys: string[]): number => keys.reduce((a, k) => a + Number(r[k] ?? r[k.toLowerCase()] ?? 0), 0);

export function evaluateOverview(o: OpsOverview, t: OpsT, now: number = Date.now()): MetricTile[] {
  const unknown = pick(o.registry_transactions_open, ['UNKNOWN', 'RECONCILING', 'MANUAL_REVIEW']);
  const openTx = sumValues(o.registry_transactions_open);
  const manual = pick(o.workflows_in_flight, ['manual_review']);
  const inFlight = sumValues(o.workflows_in_flight);
  const dead = o.queues.reduce((a, q) => a + q.dead, 0);
  const ready = o.queues.reduce((a, q) => a + q.ready, 0);
  const oldest = o.queues
    .map((q) => q.oldest_ready_at)
    .filter((x): x is string => Boolean(x))
    .sort()[0];
  const oldestAgeMin = oldest ? (now - new Date(oldest).getTime()) / 60_000 : 0;
  const gaps = sumValues(o.compliance_gaps);
  const outbox = Number(o.outbox_pending);

  return [
    {
      id: 'unknown',
      group: 'registry',
      label: t('overview.tile.unknown.label'),
      value: formatCount(unknown),
      status: countStatus(unknown, 0, 5),
      caption: t('overview.tile.unknown.caption', { open: formatCount(openTx) }),
      alert: t('overview.tile.unknown.alert'),
      to: '/ops/registries',
      toLabel: t('overview.tile.unknown.link'),
      alertName: 'RegistryUnknownTransactionsRising',
    },
    {
      id: 'recon',
      group: 'registry',
      label: t('overview.tile.recon.label'),
      value: formatCount(o.reconciliation_issues_open),
      status: countStatus(o.reconciliation_issues_open, 0, 25),
      caption: t('overview.tile.recon.caption'),
      alert: t('overview.tile.recon.alert'),
      to: '/registry/reconciliation',
      toLabel: t('overview.tile.recon.link'),
      alertName: 'ReconciliationBacklogHigh',
    },
    {
      id: 'workflows',
      group: 'registry',
      label: t('overview.tile.workflows.label'),
      value: formatCount(manual),
      status: countStatus(manual, 0, 10),
      caption: t('overview.tile.workflows.caption', { inFlight: formatCount(inFlight) }),
      alert: t('overview.tile.workflows.alert'),
      to: '/ops/workflows?state=MANUAL_REVIEW',
      toLabel: t('overview.tile.workflows.link'),
      alertName: 'WorkflowsStuckManualReview',
    },
    {
      id: 'ledger',
      group: 'money',
      label: t('overview.tile.ledger.label'),
      value: formatCount(o.ledger_unbalanced_entries),
      status: o.ledger_unbalanced_entries > 0 ? 'crit' : 'ok',
      caption: t('overview.tile.ledger.caption'),
      alert: t('overview.tile.ledger.alert'),
      to: '/ledger',
      toLabel: t('overview.tile.ledger.link'),
      alertName: 'LedgerImbalanceNonZero',
    },
    {
      id: 'compliance',
      group: 'compliance',
      label: t('overview.tile.compliance.label'),
      value: formatCount(gaps),
      status: gaps > 0 ? 'warn' : 'ok',
      caption: gaps > 0 ? t('overview.tile.compliance.caption') : t('overview.tile.compliance.captionNone'),
      alert: t('overview.tile.compliance.alert'),
      to: '/compliance/vnnic/coverage',
      toLabel: t('overview.tile.compliance.link'),
    },
    {
      id: 'incidents',
      group: 'response',
      label: t('overview.tile.incidents.label'),
      value: formatCount(o.incidents_open),
      status: o.incidents_open > 0 ? 'warn' : 'ok',
      caption: t('overview.tile.incidents.caption'),
      alert: t('overview.tile.incidents.alert'),
      to: '/ops/incidents',
      toLabel: t('overview.tile.incidents.link'),
    },
    {
      id: 'queues',
      group: 'platform',
      label: t('overview.tile.queues.label'),
      value: formatCount(ready),
      status: dead > 0 ? 'crit' : oldestAgeMin > 15 ? 'warn' : 'ok',
      caption: oldest
        ? t('overview.tile.queues.caption', { age: formatRelative(oldest, now), dead: formatCount(dead) })
        : t('overview.tile.queues.captionIdle', { dead: formatCount(dead) }),
      alert: dead > 0 ? t('overview.tile.queues.alertDead', { count: dead }) : t('overview.tile.queues.alertOld'),
      to: '/ops/queues',
      toLabel: t('overview.tile.queues.link'),
      alertName: 'CriticalQueueAgeHigh',
    },
    {
      id: 'outbox',
      group: 'platform',
      label: t('overview.tile.outbox.label'),
      value: formatCount(outbox),
      status: countStatus(outbox, 20, 500),
      caption: t('overview.tile.outbox.caption'),
      alert: t('overview.tile.outbox.alert'),
      to: '/ops/queues',
      toLabel: t('overview.tile.outbox.link'),
      alertName: 'OutboxBacklog',
    },
  ];
}

// ---------------------------------------------------------------------------
// SLO arithmetic (shared with the SLO page)

/** Objectives are written as ratios (0.999) or percentages (99.9); normalise to a ratio. */
export function ratio(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  return n > 1 ? n / 100 : n;
}

/** Share of the error budget still unspent over the window: 1 - (1 - current) / (1 - objective). */
export function budgetRemaining(s: Slo): number | null {
  const objective = ratio(s.objective);
  const current = ratio(s.current ?? null);
  if (objective === null || current === null || objective >= 1) return null;
  return 1 - (1 - current) / (1 - objective);
}

export function budgetTone(remaining: number | null): Tone {
  if (remaining === null) return 'neutral';
  if (remaining <= 0) return 'crit';
  return remaining <= 0.25 ? 'warn' : 'ok';
}

// ---------------------------------------------------------------------------
// "Needs attention now"

export type AttentionSource = 'alert' | 'incident' | 'indicator' | 'slo' | 'service';

export interface AttentionItem {
  id: string;
  tone: Tone;
  source: AttentionSource;
  title: string;
  detail: string;
  to: string;
  /** Raw code shown next to the title (alert name, severity). */
  code?: string;
  since?: string;
}

const TONE_RANK: Record<Tone, number> = { crit: 0, serious: 1, warn: 2, info: 3, pending: 4, neutral: 5, ok: 6 };
const SOURCE_RANK: Record<AttentionSource, number> = { incident: 0, alert: 1, indicator: 2, service: 3, slo: 4 };
const AGGREGATE_TILES = new Set(['alerts', 'services', 'slo']);
const ALERT_TONE: Record<string, Tone> = { P0: 'crit', P1: 'serious', P2: 'warn', P3: 'info' };

export interface AttentionInput {
  tiles: MetricTile[];
  alerts?: AlertmanagerAlert[];
  incidents?: Incident[];
  services?: OpsService[];
  slos?: Slo[];
  t: OpsT;
  label: (group: 'incidentStatus', value: string) => string;
  now?: number;
}

/**
 * Everything that is not healthy, most severe first. An indicator whose matching
 * alert is firing is folded into that alert (the alert links to the indicator's page).
 */
export function attentionItems({ tiles, alerts = [], incidents = [], services = [], slos = [], t, label, now = Date.now() }: AttentionInput): AttentionItem[] {
  const items: AttentionItem[] = [];
  const active = alerts.filter((a) => a.status.state === 'active');
  const firing = new Set(active.map((a) => a.labels.alertname));

  for (const i of incidents) {
    if (i.status === 'RESOLVED') continue;
    items.push({
      id: `incident/${i.id}`,
      tone: ALERT_TONE[i.severity] ?? 'warn',
      source: 'incident',
      title: i.title,
      code: i.severity,
      detail: t('overview.attention.incidentDetail', { status: label('incidentStatus', i.status), ago: formatRelative(i.started_at, now) }),
      to: `/ops/incidents/${encodeURIComponent(i.id)}`,
      since: i.started_at,
    });
  }

  for (const a of active) {
    const name = a.labels.alertname ?? '';
    const severity = a.labels.severity ?? 'P3';
    const tile = tiles.find((x) => x.alertName === name);
    items.push({
      id: `alert/${a.fingerprint}`,
      tone: ALERT_TONE[severity] ?? 'info',
      source: 'alert',
      title: a.annotations.summary ?? name,
      code: severity,
      detail: t('overview.attention.alertDetail', { name, ago: formatRelative(a.startsAt, now) }),
      to: tile ? tile.to : name ? `/ops/alerts/${encodeURIComponent(name)}` : '/ops/alerts',
      since: a.startsAt,
    });
  }

  for (const tile of tiles) {
    if (tile.status !== 'warn' && tile.status !== 'crit') continue;
    if (tile.alertName && firing.has(tile.alertName)) continue;
    // Aggregates whose members are listed one by one (incidents, alerts, services, SLOs).
    if (AGGREGATE_TILES.has(tile.id) || (tile.id === 'incidents' && incidents.length)) continue;
    items.push({
      id: `tile/${tile.id}`,
      tone: STATUS_TONE[tile.status],
      source: 'indicator',
      title: `${tile.label}: ${tile.value}`,
      detail: tile.alert ?? tile.caption,
      to: tile.to,
    });
  }

  const byService = new Map<string, { up: number; total: number }>();
  for (const s of services) {
    const key = s.service ?? '—';
    const g = byService.get(key) ?? { up: 0, total: 0 };
    g.total += 1;
    if (s.up) g.up += 1;
    byService.set(key, g);
  }
  for (const [service, g] of byService) {
    if (g.up === g.total) continue;
    items.push({
      id: `service/${service}`,
      tone: g.up === 0 ? 'crit' : 'warn',
      source: 'service',
      title: t('overview.attention.serviceDown', { service, down: g.total - g.up, total: g.total }),
      detail: t('overview.attention.serviceDetail'),
      to: `/ops/services/${encodeURIComponent(service)}`,
    });
  }

  for (const s of slos) {
    const remaining = budgetRemaining(s);
    if (remaining === null || remaining > 0.25) continue;
    const name = s.name ?? s.id ?? 'SLO';
    items.push({
      id: `slo/${s.id ?? name}`,
      tone: remaining <= 0 ? 'crit' : 'warn',
      source: 'slo',
      title: remaining <= 0 ? t('overview.attention.sloExhausted', { name }) : t('overview.attention.sloLow', { name, remaining: formatPercent(remaining, 0) }),
      detail: t('overview.attention.sloDetail', {
        objective: formatPercent(ratio(s.objective), 2),
        current: formatPercent(ratio(s.current ?? null), 3),
        window: s.window ?? '—',
      }),
      to: '/ops/slo',
    });
  }

  return items.sort(
    (a, b) =>
      TONE_RANK[a.tone] - TONE_RANK[b.tone] ||
      SOURCE_RANK[a.source] - SOURCE_RANK[b.source] ||
      (a.since && b.since ? a.since.localeCompare(b.since) : 0),
  );
}
